import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import ResponsiveNavbar from '../components/shared/ResponsiveNavbar';
import Toast from '../components/shared/Toast';
import { useCart } from '../context/CartContext';
import { useAdmin } from '../context/AdminContext';
import { useOAuth } from '../context/OAuthContext';
import { textures as defaultTextures } from '../data/products';
import { products as defaultProducts } from '../data/products';
import '../App.css';
import './Checkout.css';

const Checkout = () => {
  const { cart, updateQuantity, removeFromCart, getCartTotal, clearCart, validateAndUpdateCart } = useCart();
  const { products: adminProducts, textures: adminTextures, isInitialized: adminInitialized, refreshData } = useAdmin();
  const { currentUser: oauthUser, login } = useOAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [toastMessage, setToastMessage] = useState(null);
  const [toastType, setToastType] = useState('warning');

  const products = adminInitialized && adminProducts.length > 0 ? adminProducts : defaultProducts;
  const textures = useMemo(() => {
    if (Array.isArray(adminTextures) && adminTextures.length > 0) {
      const texturesObj = {};
      adminTextures.forEach(texture => {
        texturesObj[texture.id] = { name: texture.name, color: texture.color };
      });
      return { ...defaultTextures, ...texturesObj };
    }
    return defaultTextures;
  }, [adminTextures]);

  const menuItems = [
    { type: 'link', label: 'Home', to: '/' },
    { type: 'link', label: 'Shop', to: '/shop' },
    { type: 'link', label: 'Account', to: '/account' }
  ];
  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    address: '',
    city: '',
    phone: '',
  });
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [errors, setErrors] = useState({});
  const [isProcessing, setIsProcessing] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [discountCode, setDiscountCode] = useState('');
  const [appliedDiscount, setAppliedDiscount] = useState(null);
  const [discountError, setDiscountError] = useState('');
  const [isValidatingDiscount, setIsValidatingDiscount] = useState(false);

  React.useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.pageYOffset > 50);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  React.useEffect(() => {
    if (cart.length === 0) {
      navigate('/shop');
    }
  }, [cart, navigate]);

  useEffect(() => {
    const validateCart = async () => {
      if (adminInitialized) {
        await refreshData();
      }

      setTimeout(() => {
        const currentProducts = adminInitialized && adminProducts.length > 0 ? adminProducts : defaultProducts;
        const currentTextures = adminInitialized && Array.isArray(adminTextures) && adminTextures.length > 0 ? adminTextures : [];
        if (cart.length > 0 && currentProducts.length > 0) {
          const hasChanges = validateAndUpdateCart(currentProducts, currentTextures);
          if (hasChanges) {
            setToastType('warning');
            setToastMessage('One or more items in your cart have been updated or removed due to product changes.');

            setTimeout(() => {
              setToastMessage(null);
            }, 5000);
          }
        }
      }, 100);
    };

    validateCart();
  }, [location.pathname]);

  useEffect(() => {
    if (oauthUser && oauthUser.activeCharacterId) {
      const activeCharacter = oauthUser.characters?.find(
        c => c.id === oauthUser.activeCharacterId || (c.isActive === true || c.isActive === 1)
      );

      if (activeCharacter) {
        setFormData(prev => ({
          ...prev,
          email: activeCharacter.email !== null && activeCharacter.email !== undefined ? activeCharacter.email : prev.email,
          firstName: activeCharacter.firstname || prev.firstName,
          lastName: activeCharacter.lastname || prev.lastName,
          address: activeCharacter.address || prev.address,
          city: activeCharacter.city !== null && activeCharacter.city !== undefined ? activeCharacter.city : prev.city,
          phone: activeCharacter.phone || prev.phone,
        }));
      }
      setShowLoginPrompt(false);
    } else if (!oauthUser && cart.length > 0) {
      const hasSeenPrompt = sessionStorage.getItem('checkout_login_prompt_seen');
      if (!hasSeenPrompt) {
        setShowLoginPrompt(true);
        sessionStorage.setItem('checkout_login_prompt_seen', 'true');
      }
    }
  }, [oauthUser, cart.length]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
    if (submitError) {
      setSubmitError('');
    }
  };

  const handleDiscountCodeChange = (e) => {
    setDiscountCode(e.target.value.toUpperCase());
    setDiscountError('');
    if (appliedDiscount) {
      setAppliedDiscount(null);
    }
  };

  const handleApplyDiscount = async () => {
    if (!discountCode.trim()) {
      setDiscountError('Please enter a discount code');
      return;
    }

    setIsValidatingDiscount(true);
    setDiscountError('');

    try {
      const { discountCodesAPI } = await import('../services/api');
      const total = getCartTotal();
      const response = await discountCodesAPI.validate(discountCode.trim(), total);

      if (response.success && response.data) {
        setAppliedDiscount({
          code: response.data.code,
          discountAmount: parseFloat(response.data.discountAmount) || 0,
          finalTotal: parseFloat(response.data.finalTotal) || total
        });
        setDiscountError('');
      }
    } catch (error) {
      setDiscountError(error.message || 'Invalid discount code');
      setAppliedDiscount(null);
    } finally {
      setIsValidatingDiscount(false);
    }
  };

  const handleRemoveDiscount = () => {
    setDiscountCode('');
    setAppliedDiscount(null);
    setDiscountError('');
  };

  const getFinalTotal = () => {
    const subtotal = getCartTotal();
    if (appliedDiscount) {
      return appliedDiscount.finalTotal;
    }
    return subtotal;
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.email.trim()) {
      newErrors.email = 'Discord username is required';
    }

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!formData.address.trim()) {
      newErrors.address = 'Address is required';
    }

    if (!formData.city.trim()) {
      newErrors.city = 'City is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    if (products.length > 0) {
      const currentTextures = adminInitialized && Array.isArray(adminTextures) && adminTextures.length > 0 ? adminTextures : [];
      const hasChanges = validateAndUpdateCart(products, currentTextures);
      if (hasChanges) {
        setToastType('warning');
        setToastMessage('One or more items in your cart have been updated or removed due to product changes. Please review your cart before completing your order.');
        setIsProcessing(false);
        return;
      }
    }

    setIsProcessing(true);

    try {
      const { ordersAPI } = await import('../services/api');

      const orderData = {
        customer: formData,
        items: cart,
        total: getCartTotal()
      };

      if (appliedDiscount) {
        try {
          const { discountCodesAPI } = await import('../services/api');
          const codesResponse = await discountCodesAPI.getAll();
          if (codesResponse.success) {
            const code = codesResponse.data.find(c => c.code === appliedDiscount.code);
            if (code) {
              orderData.discountCodeId = code.id;
              orderData.discountAmount = parseFloat(appliedDiscount.discountAmount) || 0;
            }
          }
        } catch (err) {
          console.error('Error getting discount code ID:', err);
        }
      }

      if (oauthUser) {
        orderData.oauthUserId = oauthUser.id;
        if (oauthUser.activeCharacterId) {
          orderData.characterId = oauthUser.activeCharacterId;
        }
      }

      const response = await ordersAPI.create(orderData);

      if (response.success && response.data) {
        sessionStorage.setItem('vague_last_order', JSON.stringify(response.data));

        const orderId = response.data.order_id || response.data.id;
        const orderTotal = response.data.total || getFinalTotal();

        console.log('Order created successfully:', response.data);

        if (orderId) {
          const fleecaApiKey = process.env.REACT_APP_FLEECA_API_KEY || '2V6iKuuW4aifBDvuuPJn6yphQLqde0seKfXdjHH6UjQZudr5eNLNJk5t6syqK807';
          const fleecaGatewayUrl = process.env.REACT_APP_FLEECA_GATEWAY_URL || 'http://banking.gta.world/gateway/';

          const fleecaUrl = `${fleecaGatewayUrl}${fleecaApiKey}/0/${orderTotal}`;

          window.open(fleecaUrl, '_blank');

          setTimeout(() => {
            navigate(`/confirmation/${orderId}`, { replace: true });
          }, 100);
        } else {
          console.error('Order ID not found in response:', response.data);
          setSubmitError('Order created but could not redirect. Please check your orders.');
          setIsProcessing(false);
        }
      } else {
        console.error('Order creation failed:', response);
        setSubmitError(response.message || 'Failed to create order. Please try again.');
        setIsProcessing(false);
      }
    } catch (error) {
      console.error('Order creation error:', error);
      setSubmitError(error.message || 'Failed to create order. Please try again.');
      setIsProcessing(false);
    }
  };

  const total = getCartTotal();

  if (cart.length === 0) {
    return null;
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.43, 0.13, 0.23, 0.96] }}
      className="checkout-page"
    >
      {}
      <ResponsiveNavbar
        menuItems={menuItems}
        logo="VELOURA"
        logoLink="/"
        scrolled={scrolled}
      />

      {}
      {showLoginPrompt && !oauthUser && (
        <div className="checkout-login-prompt">
          <div className="checkout-login-prompt-content">
            <h3>Login to Track Your Purchases</h3>
            <p>Create an account or log in to keep track of your orders and applications.</p>
            <div className="checkout-login-prompt-buttons">
                <button
                  onClick={() => {
                    setShowLoginPrompt(false);
                    login(window.location.pathname + window.location.search);
                  }}
                  className="checkout-login-button"
                >
                  Login with GTA World
                </button>
              <button
                onClick={() => {
                  setShowLoginPrompt(false);
                  sessionStorage.setItem('checkout_login_prompt_seen', 'true');
                }}
                className="checkout-dismiss-button"
              >
                Continue as Guest
              </button>

            </div>
          </div>
        </div>
      )}
      <div className="checkout-container">
        <div className="checkout-content">
          {}
          <div className="checkout-order-summary">
            <h2 className="checkout-section-title">Order Summary</h2>
            <div className="checkout-items">
              {cart.map((item, index) => (
                <div key={`${item.productId}-${item.texture}-${index}`} className="checkout-item">
                  <img
                    src={item.productImage}
                    alt={item.productName}
                    className="checkout-item-image"
                  />
                  <div className="checkout-item-info">
                    <h4 className="checkout-item-name">{item.productName}</h4>
                    <p className="checkout-item-texture">
                      {textures[item.texture]?.name || item.texture}
                    </p>
                    {item.bundleDiscountApplied && (
                      <span className="checkout-item-bundle">Color / Style set · {item.bundleDiscountPercent}% off</span>
                    )}
                    <div className="checkout-item-controls">
                      <div className="quantity-controls">
                        <button
                          onClick={() => updateQuantity(item.productId, item.texture, item.quantity - 1)}
                          className="quantity-button"
                        >
                          −
                        </button>
                        <span className="quantity-value">{item.quantity}</span>
                        <button
                          onClick={() => updateQuantity(item.productId, item.texture, item.quantity + 1)}
                          className="quantity-button"
                        >
                          +
                        </button>
                      </div>
                      <button
                        onClick={() => removeFromCart(item.productId, item.texture)}
                        className="remove-button"
                        aria-label="Remove item"
                      >
                        ×
                      </button>
                    </div>
                  </div>
                  <div className="checkout-item-price">
                    ${((parseFloat(item.price) || 0) * item.quantity).toFixed(2)}
                  </div>
                </div>
              ))}
            </div>
            {}
            <div className="checkout-discount-section">
              <div className="discount-code-input-group">
                <input
                  type="text"
                  className="discount-code-input"
                  placeholder="Discount Code"
                  value={discountCode}
                  onChange={handleDiscountCodeChange}
                  disabled={isValidatingDiscount || isProcessing || !!appliedDiscount}
                />
                {!appliedDiscount ? (
                  <button
                    type="button"
                    className="discount-code-button"
                    onClick={handleApplyDiscount}
                    disabled={isValidatingDiscount || isProcessing || !discountCode.trim()}
                  >
                    {isValidatingDiscount ? 'Validating...' : 'Apply'}
                  </button>
                ) : (
                  <button
                    type="button"
                    className="discount-code-remove-button"
                    onClick={handleRemoveDiscount}
                    disabled={isProcessing}
                  >
                    Remove
                  </button>
                )}
              </div>
              {discountError && (
                <div className="discount-error">{discountError}</div>

              )}
              {appliedDiscount && (
                <div className="discount-success">
                  Discount applied: {appliedDiscount.code} (-${appliedDiscount.discountAmount.toFixed(2)})
                </div>
              )}
            </div>
            {appliedDiscount && (
              <div className="checkout-subtotal">
                <span>Subtotal:</span>
                <span>${total.toFixed(2)}</span>
              </div>

            )}
            {appliedDiscount && (
              <div className="checkout-discount">
                <span>Discount ({appliedDiscount.code}):</span>
                <span>-${appliedDiscount.discountAmount.toFixed(2)}</span>
              </div>
            )}
            <div className="checkout-total">
              <span>Total:</span>
              <span>${getFinalTotal().toFixed(2)}</span>

            </div>
          </div>
          {}
          <div className="checkout-form-container">
            <h2 className="checkout-section-title">Shipping Information</h2>
            <form onSubmit={handleSubmit} className="checkout-form">
              <div className="form-group">
                <label htmlFor="email">((Discord)) *</label>
                <input
                  type="text"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={errors.email ? 'error' : ''}
                  placeholder="Discord username"
                />
                <span className="error-message">{errors.email || ''}</span>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="firstName">First Name *</label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    className={errors.firstName ? 'error' : ''}
                    placeholder="John"
                  />
                  <span className="error-message">{errors.firstName || ''}</span>
                </div>
                <div className="form-group">
                  <label htmlFor="lastName">Last Name *</label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    className={errors.lastName ? 'error' : ''}
                    placeholder="Doe"
                  />
                  <span className="error-message">{errors.lastName || ''}</span>
                </div>
              </div>
              <div className="form-group">
                <label htmlFor="address">Address *</label>
                <input
                  type="text"
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  className={errors.address ? 'error' : ''}
                  placeholder="123 Main Street"
                />
                <span className="error-message">{errors.address || ''}</span>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="city">City *</label>
                  <select
                    id="city"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    className={errors.city ? 'error' : ''}
                  >
                    <option value="">Select a city</option>
                    <option value="Los Santos">Los Santos</option>
                    <option value="Sandy Shores">Sandy Shores</option>
                    <option value="Paleto Bay">Paleto Bay</option>
                  </select>
                  <span className="error-message">{errors.city || ''}</span>
                </div>

                <div className="form-group">
                  <label htmlFor="phone">Phone</label>

                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="1234567"
                  />
                </div>

              </div>


              {submitError && (
                <div className="error-message" style={{ marginBottom: 0, color: '#d32f2f', textAlign: 'center' }}>
                  {submitError}
                </div>

              )}

              <div style={{
                marginBottom: 0,
                padding: '0.75rem',
                backgroundColor: 'rgba(211, 47, 47, 0.1)',
                border: '1px solid #d32f2f',
                borderRadius: '4px',
                color: '#d32f2f',
                fontSize: '0.9rem',
                textAlign: 'center',
                fontWeight: 500,
                lineHeight: '1.4'
              }}>
                You should be logged into <a href="https://banking.gta.world/login" target="_blank" rel="noopener noreferrer" style={{ color: '#d32f2f', textDecoration: 'underline', fontWeight: 600 }}>Fleeca</a> before placing an order!

              </div>


              <button
                type="submit"
                className="checkout-submit-button"
                disabled={isProcessing}
              >
                {isProcessing ? 'Processing...' : 'Complete Order'}
              </button>

            </form>

          </div>

        </div>

      </div>


      {}
      {toastMessage && (
        <Toast
          message={toastMessage}
          type={toastType}
          onClose={() => setToastMessage(null)}
          duration={5000}
        />

      )}
    </motion.div>

  );
};

export default Checkout;

